<template>
    <div class="modal" tabindex="-1" role="dialog" id="c2b-seller-account-statement-list-modal">
        <div class="modal-dialog modal-xl" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <div class="float-left">
                        <h5 class="modal-title">Extrato</h5>
                    </div>
                </div>
                <div class="modal-body">
                    <div class="row">
                        <div class="col-12 col-md-5 form-group">
                            <label>Nome</label>
                            <p>{{seller.realName}}</p>                                    
                            <small class="text-muted">{{seller.name}}</small>                                    
                        </div>

                        <div class="col-12 col-md-2 form-group">
                            <label>Código C2B</label>
                            <p>{{seller.id}}</p>
                        </div>

                        <div class="col-12 col-md-2 form-group text-right">
                            <label>Saldo atual</label>
                            <p>{{sellerCurrentBalance.balance | currency}}</p>
                        </div>

                        <div class="col-12 col-md-3 mt-2 form-group text-right">
                            <json-to-csv
                                class="btn btn-outline-secondary ml-3"
                                :data="accountStatementListCsv"
                                name="extrato.csv">
                                Exportar para CSV
                            </json-to-csv>
                        </div>
                    </div>

                    <div class="row form-filter">
                        <div class="col-12 col-md-3 form-group">
                            <label>Data inicial</label>
                            <v-date-picker 
                                :input-props="{'class': 'form-control'}" 
                                popover-visibility="focus"
                                v-model="sellerAccountStatementFilterForm.dateStart" />
                        </div>

                        <div class="col-12 col-md-3 form-group">
                            <label>Data final</label>
                            <v-date-picker 
                                :input-props="{'class': 'form-control'}" 
                                popover-visibility="focus"
                                v-model="sellerAccountStatementFilterForm.dateEnd" />
                        </div>

                        <div class="col-12 col-md-6 text-right">
                            <button type="button" 
                                :disabled="loadingSellerAccountStatementFilter"
                                class="btn btn-outline-primary"
                                @click="getSellerAccountStatementWithFilter(sellerAccountStatementFilterForm)">
                                Filtrar
                                <Loading v-show="loadingSellerAccountStatementFilter" />
                            </button>
                        </div>
                    </div>

                    <div class="row" v-show="loadingSellerAccountStatementFilter">
                        <Loading class="mt-3 text-left text-muted" />
                    </div>

                    <div class="row" v-show="!accountStatementList.length && !loadingSellerAccountStatementFilter">
                        <p class="text-muted ml-3">Nenhuma informação encontrada.</p>
                    </div>

                    <div class="row" v-show="!!accountStatementList.length && !loadingSellerAccountStatementFilter">
                        <div class="account-statement-labels-container">
                            <label class="account-statement-label-date">Data</label>
                            <label class="account-statement-label-description">Descrição</label>
                            <label class="account-statement-label-value">Valor</label>
                            <label class="account-statement-label-total">Saldo</label>
                        </div>

                        <div class="account-statement-list-container">
                            <div class="account-statement-info-container" v-for="(accountInfo, index) in accountStatementList" :key="index">
                                <div class="account-statement-tr-like">
                                    <p class="account-statement-date">{{accountInfo.date | dateFilter}}</p>
                                    <p class="account-statement-description">{{accountInfo.description}}</p>
                                    <p class="account-statement-amount text-right">{{accountInfo.amount | currency}}</p>
                                    <p class="account-statement-balance text-right">{{accountInfo.balance | currency}}</p>
                                    <fa icon="chevron-down" 
                                        class="account-statement-toggle-icon" 
                                        v-show="accountInfo.hasBillingCommission()"
                                        @click="toggleSellerAccountStatementVisibility(accountInfo)"
                                        :class="{'visible': accountInfo.visible}" />
                                </div>

                                <div class="row account-statement-more-info" v-if="accountInfo.hasBillingCommission() && accountInfo.visible">
                                    <div class="col-12 mb-3">
                                        <label>Descrição</label>
                                        <p>{{accountInfo.billingCommission.billing.description}}</p>
                                    </div>

                                    <div class="col-12 col-md-3 mb-0">
                                        <label>Total pago Muven</label>
                                        <p>{{accountInfo.billingCommission.billing.total | currency}}</p>
                                    </div>

                                    <div class="col-12 col-md-3 mb-0">
                                        <label>Status</label>
                                        <p>
                                            <span v-show="accountInfo.billingCommission.billing.hasBeenCanceled()">
                                                <span class="badge badge-danger">Cancelado</span>
                                            </span>
                                            <span v-show="accountInfo.billingCommission.billing.hasBilletBeenPaid() && !accountInfo.billingCommission.billing.hasBeenCanceled()">
                                                <span class="badge badge-success">Pago</span>
                                            </span>
                                            <span v-show="!accountInfo.billingCommission.billing.hasBilletBeenPaid() && !accountInfo.billingCommission.billing.isOverdue() && !accountInfo.billingCommission.billing.hasBeenCanceled()">
                                                <span class="badge badge-info">Em aberto</span>
                                            </span>
                                            <span v-show="accountInfo.billingCommission.billing.isOverdue()">
                                                <span class="badge badge-danger">Vencido</span>
                                            </span>
                                        </p>
                                    </div>

                                    <div class="col-12 col-md-3 mb-0">
                                        <label>Pagamento</label>
                                        <p>{{accountInfo.billingCommission.billing.paymentDate | dateFilter}}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" 
                        class="btn btn-outline-secondary" 
                        @click="closeAccountStatementListModal()">Fechar</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import ModalService from '@/utils/services/ModalService'
import sellerService from '../services/SellerService';
import { SellerCurrentBalanceModel } from '../models/SellerCurrentBalanceModel';
import { SellerAccountStatementListFilterFormModel } from '../models/SellerAccountStatementListFilterFormModel'
import { SellerModel } from '../models/SellerModel';
import { SellerAccountStatementModel } from '../../seller/models/SellerAccountStatementModel';
import { dateFilter } from '@/utils/filters/DateTimeFilter'

const ID_SELLER_ACCOUNT_STATEMENT_LIST_MODAL = '#c2b-seller-account-statement-list-modal'

export default {
    name: 'SellerAccountStatementCmp',    
    filters: {
        dateFilter,
    },
    data() {
        return {
            loadingSellerAccountStatementFilter: false,
            sellerAccountStatementFilterForm: new SellerAccountStatementListFilterFormModel(),
            seller: new SellerModel(),
            accountStatementList: [],
            sellerCurrentBalance: new SellerCurrentBalanceModel(), 
        }
    },
    methods: {
        async openAccountStatementListModal(seller) {
            seller.loadingListAction = true;

            this.seller = cloneDeep(seller);

            try {
                this.accountStatementList = await this._getAndParseSellerAccountStatement(this.seller, this.sellerAccountStatementFilterForm)      
                this.sellerCurrentBalance = await sellerService.getSellersCurrentBalance(this.seller.id)           
                ModalService.show(ID_SELLER_ACCOUNT_STATEMENT_LIST_MODAL)
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            seller.loadingListAction = false
        },
        async _getAndParseSellerAccountStatement(seller, sellerAccountStatementFilterForm) {
            const accountStatementList = await sellerService.getSellersAccountStatement(seller.id, sellerAccountStatementFilterForm)

            return accountStatementList.map((as) => {
                return new SellerAccountStatementModel(Object.assign({},  as, {visible: false}))
            })
        },
        async getSellerAccountStatementWithFilter(sellerAccountFilterForm) {
            this.loadingSellerAccountStatementFilter = true

            try {
                this.accountStatementList = await this._getAndParseSellerAccountStatement(this.seller, sellerAccountFilterForm)
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            this.loadingSellerAccountStatementFilter = false
        },
        closeAccountStatementListModal() {
            ModalService.hide(ID_SELLER_ACCOUNT_STATEMENT_LIST_MODAL)
        },
        async toggleSellerAccountStatementVisibility(accountInfo) {
            accountInfo.visible = !accountInfo.visible            
        },
    },
    computed: {
        accountStatementListCsv() {
            return this.accountStatementList.map((s) => {
                return {
                    data: dateFilter(s.date),
                    descricao: s.description,
                    valor: s.amount,
                    saldo: s.balance,
                    descricao_cobranca: !!s.billingCommission ? s.billingCommission.billing.description : '-',
                    total_pago_muven: !!s.billingCommission ? s.billingCommission.billing.total : 0,
                    pago_em: !!s.billingCommission ? dateFilter(s.billingCommission.billing.paymentDate) : '-',
                }
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.form-filter {
    margin: 16px auto 16px;
    width: 100%;
    padding: 8px 0;
    border: 2px dashed #eceeef;
    background-color: #f9f9fa;
    border-radius: 4px;

    .btn {
        margin-top: 26px;
    }
}

.account-statement-info-container,
.account-statement-labels-container {
    margin: 0 auto;
    width: calc(100% - 32px);
}

.account-statement-labels-container {
    label {
        padding: 8px;

        &:first-of-type {
            padding-left: 0;
        }

        &:last-of-type {
            padding-right: 0;
        }

        &.account-statement-label-date {
            float: left;
            width: 100px;
            margin-bottom: 0;
        }

        &.account-statement-label-value {
            float: left;
            text-align: right;
            width: 96px;
            margin-bottom: 0;   
        }

        &.account-statement-label-total {
            float: left;
            text-align: right;
            width: 95px;
            margin-bottom: 0;   
        }

        &.account-statement-label-description {
            float: left;
            width: calc(100% - 350px);
            margin-bottom: 0;   
        }
    }
}

.account-statement-list-container {
    max-height: 350px;
    overflow-y: auto;
    width: 100%;

    .account-statement-info-container {
        padding: 8px;
        border: 1px solid #eceeef;
        border-bottom-color: transparent;
        position: relative;

        &:last-of-type {
            border-bottom-color: #eceeef;
        }

        &:nth-child(odd) {
            background-color: rgba(0,0,0,.05);
        }

        .account-statement-tr-like {
            overflow: hidden;

            .account-statement-date {
                float: left;
                width: 100px;
                margin-bottom: 0;
            }

            .account-statement-description {
                float: left;
                width: calc(100% - 350px); // 100px from the date and 50px from the toggle icon
                margin-bottom: 0;
            }

            .account-statement-amount {
                float: left;
                width: 100px;
                margin-bottom: 0;
            }

            .account-statement-balance {
                float: left;
                width: 100px;
                margin-bottom: 0;
            }

            .account-statement-toggle-icon {
                transition: transform .3s ease;
                position: absolute;
                top: 8px;
                right: 8px;
                color: #777;
                width: 25px;
                height: 25px;
                padding: 6px;
                cursor: pointer;
                
                &.visible {
                    transform: rotate(180deg);
                }
            }
        }

        .account-statement-more-info {
            margin-top: 15px;
        }
    }
}
</style>